import { ArrowRightIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/16/solid';

import SubscribeExternalLink from '@/components/shared/SubscribeExternalLink';
import { CBBIcon, CFBIcon, NFLIcon } from '@/pages/order/components/ProductIcons';
import { isIosNative } from '@/renderer/utils/isIosNative';
import {
  SINCE_2020_LABEL,
  type SportRecord,
  type SportRecordKey,
  formatSportRecord,
} from '../sportRecords';

type IconType = (props: React.ComponentPropsWithoutRef<'svg'>) => JSX.Element;

/** A sport's since-2020 record, as rendered. */
interface HeadlineRecord {
  record: string;
  winRate: string;
  units: string;
}

interface Pillar {
  key: SportRecordKey;
  name: string;
  Icon: IconType;
  tagline: string;
  resultsHref: string;
  /** Unset when the live record could not be read: the card shows no numbers. */
  record?: HeadlineRecord;
  /** Only set while the sport has a publicly purchasable offer. */
  buyHref?: string;
}

/**
 * The three sports as equal cards: each sport's since-2020 record (read live),
 * its results, and for NFL a Join now link while NFL is on sale.
 */
export default function SportPillars({
  nflOnSale,
  records,
}: {
  /** Whether NFL has a publicly purchasable offer (never the viewer's own ownership). */
  nflOnSale: boolean;
  records?: Partial<Record<SportRecordKey, SportRecord>>;
}) {
  const recordFor = (key: SportRecordKey) => {
    const record = records?.[key];
    return record ? formatSportRecord(record) : undefined;
  };
  const pillars: Pillar[] = [
    {
      key: 'nfl',
      name: 'NFL',
      Icon: NFLIcon,
      tagline: 'Sides, totals and halves, preseason through the Super Bowl.',
      resultsHref: '/archive/nfl/season/last-3',
      record: recordFor('nfl'),
      buyHref: nflOnSale ? '/order/nfl' : undefined,
    },
    {
      key: 'cfb',
      name: 'College Football',
      Icon: CFBIcon,
      tagline: 'FBS and FCS sides and totals.',
      resultsHref: '/archive/cfb/season/last-3',
      record: recordFor('cfb'),
    },
    {
      // Deliberately no buy link, price or date, whatever the product data says,
      // until this is changed on purpose.
      key: 'cbb',
      name: 'College Basketball',
      Icon: CBBIcon,
      tagline: 'College basketball: more details soon.',
      resultsHref: '/archive/cbb/season/last-3',
      record: recordFor('cbb'),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-8">
      <div className="mb-8 text-center">
        <h2 className="font-bold text-3xl text-white md:text-4xl">Three sports, one standard</h2>
        <p className="mx-auto mt-3 max-w-prose text-zinc-400">
          NFL, college football and college basketball, every play tracked in our public results.
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {pillars.map((pillar) => (
          <PillarCard key={pillar.key} pillar={pillar} />
        ))}
      </div>
    </div>
  );
}

function PillarCard({ pillar }: { pillar: Pillar }) {
  const { Icon, record, buyHref } = pillar;
  return (
    <div
      data-testid={`sport-card-${pillar.key}`}
      className="flex flex-col gap-4 rounded-sm bg-linear-to-b from-white/5 to-transparent p-6 ring-1 ring-white/20"
    >
      <div className="flex items-center gap-3">
        <Icon className="size-10 shrink-0 fill-white" />
        <div>
          <h3 className="font-bold text-white text-xl leading-tight">{pillar.name}</h3>
          <p className="mt-0.5 text-sm text-zinc-400">{pillar.tagline}</p>
        </div>
      </div>
      {record ? (
        <div className="border-white/10 border-y py-4">
          <div className="flex flex-wrap justify-between gap-x-2 gap-y-3 whitespace-nowrap tabular-nums">
            <div>
              <div className="font-bold text-base text-white tracking-tight xl:text-lg">
                {record.record}
              </div>
              <div className="text-xs text-zinc-500">Record</div>
            </div>
            <div>
              <div className="font-bold text-base text-emerald-400 xl:text-lg">
                {record.winRate}
              </div>
              <div className="text-xs text-zinc-500">Win rate</div>
            </div>
            <div>
              <div
                className={`font-bold text-base xl:text-lg ${record.units.startsWith('-') ? 'text-zinc-300' : 'text-emerald-400'}`}
              >
                {record.units}
              </div>
              <div className="text-xs text-zinc-500">Units won</div>
            </div>
          </div>
          <div className="mt-2 text-xs text-zinc-500">{SINCE_2020_LABEL}</div>
        </div>
      ) : (
        <div className="border-white/10 border-y py-4 text-sm text-zinc-400">
          Every play is graded in our public results.
        </div>
      )}
      <div className="mt-auto flex flex-col gap-2">
        {buyHref &&
          (isIosNative() ? (
            <SubscribeExternalLink
              path={buyHref}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-sm bg-emerald-400/10 px-5 py-2.5 font-semibold text-emerald-400 text-sm ring-1 ring-emerald-400 ring-inset hover:bg-emerald-400/20"
            >
              Subscribe at raspicks.com
              <ArrowTopRightOnSquareIcon className="size-4" />
            </SubscribeExternalLink>
          ) : (
            <a
              href={buyHref}
              className="inline-flex w-full items-center justify-center rounded-sm bg-emerald-400/10 px-5 py-2.5 font-semibold text-emerald-400 text-sm ring-1 ring-emerald-400 ring-inset hover:bg-emerald-400/20"
            >
              Join now →
            </a>
          ))}
        <a
          href={pillar.resultsHref}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-sm px-5 py-2.5 font-medium text-sm text-zinc-200 ring-1 ring-white/10 ring-inset transition hover:bg-white/5 hover:text-white"
        >
          View results<span className="sr-only">: {pillar.name}</span>
          <ArrowRightIcon className="size-4" />
        </a>
      </div>
    </div>
  );
}
